(() => {
  const routeRoot = () => {
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const cartLink = () => document.querySelector('[data-nuvia-cart-link]');

  const setCartCount = async ({ bump = false } = {}) => {
    const response = await fetch(`${routeRoot()}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) return null;

    const cart = await response.json();
    document.querySelectorAll('[data-nuvia-cart-link]').forEach((link) => {
      const count = link.querySelector('[data-nuvia-cart-count]');
      link.classList.toggle('has-items', cart.item_count > 0);
      link.setAttribute('aria-label', cart.item_count > 0 ? `購物車，目前 ${cart.item_count} 件商品` : '購物車');

      if (count) {
        count.textContent = cart.item_count;
        count.hidden = cart.item_count === 0;
      }

      if (bump) {
        link.classList.remove('is-bumped');
        window.requestAnimationFrame(() => {
          link.classList.add('is-bumped');
          window.setTimeout(() => link.classList.remove('is-bumped'), 520);
        });
      }
    });

    return cart;
  };

  const showStatus = (form, message, type = 'success') => {
    const status = form.querySelector('[data-nuvia-cart-status]');
    if (!status) return;
    status.textContent = message;
    status.hidden = !message;
    status.classList.toggle('is-error', type === 'error');
  };

  const productImageUrl = (form) => {
    const productShell = form.closest('.product-shell');
    const image = productShell
      ? productShell.querySelector('.product-gallery-slide.is-active img, .product-gallery img')
      : null;
    return image ? (image.currentSrc || image.src || '') : '';
  };

  const flyToCart = (form, sourceElement) => new Promise((resolve) => {
    const target = cartLink();
    const source = sourceElement || form;

    if (
      !target ||
      !source ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      resolve(false);
      return;
    }

    const start = source.getBoundingClientRect();
    const targetAnchor = target.querySelector('.cart-icon') || target;
    const end = targetAnchor.getBoundingClientRect();
    if (!start.width || !start.height || !end.width || !end.height) {
      resolve(false);
      return;
    }

    const size = Math.min(58, Math.max(46, Math.round(Math.min(start.width, 72))));
    const startX = start.left + start.width / 2;
    const startY = start.top + start.height / 2;
    const endX = end.left + end.width / 2;
    const endY = end.top + end.height / 2;

    const flyer = document.createElement('div');
    flyer.className = 'cart-flyer';
    flyer.setAttribute('aria-hidden', 'true');
    flyer.style.width = `${size}px`;
    flyer.style.height = `${size}px`;
    flyer.style.left = `${startX - size / 2}px`;
    flyer.style.top = `${startY - size / 2}px`;

    const imageUrl = productImageUrl(form);
    if (imageUrl) {
      flyer.style.backgroundImage = `url("${imageUrl}")`;
    }

    document.body.appendChild(flyer);

    const moveX = endX - startX;
    const moveY = endY - startY;
    const lift = Math.min(120, Math.max(70, Math.abs(moveY) * 0.22));

    if (typeof flyer.animate !== 'function') {
      window.setTimeout(() => {
        flyer.remove();
        resolve(true);
      }, 520);
      return;
    }

    const animation = flyer.animate([
      {
        transform: 'translate3d(0, 0, 0) scale(.86) rotate(0deg)',
        opacity: 0
      },
      {
        transform: 'translate3d(0, -8px, 0) scale(1) rotate(0deg)',
        opacity: 1,
        offset: .12
      },
      {
        transform: `translate3d(${moveX * .42}px, ${moveY * .44 - lift}px, 0) scale(.86) rotate(-3deg)`,
        opacity: .98,
        offset: .56
      },
      {
        transform: `translate3d(${moveX * .84}px, ${moveY * .84 - 14}px, 0) scale(.42) rotate(-1deg)`,
        opacity: .9,
        offset: .86
      },
      {
        transform: `translate3d(${moveX}px, ${moveY}px, 0) scale(.12) rotate(0deg)`,
        opacity: 0
      }
    ], {
      duration: 920,
      easing: 'cubic-bezier(.18, .72, .18, 1)'
    });

    const cleanup = () => {
      flyer.remove();
      resolve(true);
    };

    animation.addEventListener('finish', cleanup, { once: true });
    animation.addEventListener('cancel', cleanup, { once: true });
  });

  const selectedVariantData = (form) => {
    const selector = form.querySelector('[data-nuvia-size-selector]');
    return selector ? selector.options[selector.selectedIndex] : form.querySelector('[name="id"]');
  };

  const syncProductSizeState = (form) => {
    const selected = selectedVariantData(form);
    const hasSelection = Boolean(selected?.value);
    const isSoldOut = hasSelection && selected.dataset.nuviaVariantAvailable === 'false';
    const rawLimit = selected?.dataset.nuviaStockLimit;
    const stockLimit = rawLimit !== undefined && rawLimit !== '' ? Math.max(0, Number(rawLimit)) : null;
    const inCart = Number(selected?.dataset.nuviaInCart || 0);
    const remaining = stockLimit === null ? null : Math.max(0, stockLimit - inCart);
    const canSubmit = hasSelection && !isSoldOut && remaining !== 0;
    const busy = form.dataset.nuviaSubmitting === 'true';
    const quantity = form.querySelector('[name="quantity"]');
    const hint = form.querySelector('[data-nuvia-quantity-hint]');

    if (quantity) {
      if (hasSelection && remaining !== null) quantity.max = String(remaining);
      else quantity.removeAttribute('max');
      quantity.disabled = !canSubmit;
      quantity.readOnly = busy || remaining === 1;
      const parsed = Math.floor(Number(quantity.value));
      const normalized = Number.isFinite(parsed) ? Math.max(1, parsed) : 1;
      quantity.value = canSubmit ? (remaining === null ? normalized : Math.min(remaining, normalized)) : 1;
    }
    if (hint) {
      hint.textContent = !hasSelection ? '請先選擇規格。'
        : isSoldOut ? '此規格目前售完。'
        : remaining === 0 ? '此規格的可購數量已在購物車中。'
        : remaining !== null ? (inCart > 0 ? `購物車已有 ${inCart} 副，還可加購 ${remaining} 副。` : `此規格最多可選 ${remaining} 副。`)
        : '';
      hint.hidden = !hint.textContent;
    }
    form.querySelectorAll('[type="submit"]').forEach((button) => {
      button.disabled = busy || !canSubmit;
    });
    const stickySubmit = form.querySelector('.product-mobile-sticky-submit');
    if (stickySubmit && !busy) {
      stickySubmit.textContent = !hasSelection ? '請先選擇規格' : isSoldOut ? '此規格已售完' : remaining === 0 ? '已達可購數量' : '加入購物車';
    }
    return canSubmit;
  };

  const syncProductCart = (form, cart) => {
    if (!cart || !Array.isArray(cart.items)) return;
    form.querySelectorAll('option[value], input[name="id"]').forEach((option) => {
      option.dataset.nuviaInCart = String(cart.items.reduce((total, item) => total + (String(item.variant_id) === option.value ? item.quantity : 0), 0));
    });
    syncProductSizeState(form);
  };

  document.querySelectorAll('[data-nuvia-product-form]').forEach((form) => {
    const sizeSelector = form.querySelector('[data-nuvia-size-selector]');
    const quantity = form.querySelector('[name="quantity"]');
    syncProductSizeState(form);
    sizeSelector?.addEventListener('change', () => {
      syncProductSizeState(form);
      showStatus(form, '');
    });
    ['input', 'change', 'blur'].forEach((eventName) => {
      quantity?.addEventListener(eventName, () => {
        if (eventName === 'input' && quantity.value === '') return;
        syncProductSizeState(form);
      });
    });
    const refreshCart = async () => {
      if (form.dataset.nuviaSubmitting === 'true') return;
      try { syncProductCart(form, await setCartCount()); } catch (_) { /* Rechecked before adding. */ }
    };
    window.addEventListener('pageshow', refreshCart);
    window.addEventListener('focus', refreshCart);
  });

  document.querySelectorAll('[data-nuvia-product-form]').forEach((form) => {
    const variantSelector = form.querySelector('[data-nuvia-size-selector]');
    const variantButtons = Array.from(form.querySelectorAll('[data-nuvia-variant-option]'));
    const fallbackLabel = form.querySelector('.product-variant-fallback-label');

    if (!variantSelector || !variantButtons.length) return;

    variantSelector.hidden = true;
    if (fallbackLabel) fallbackLabel.hidden = true;

    const syncVariantButtons = () => {
      variantButtons.forEach((button) => {
        const isSelected = button.dataset.variantId === variantSelector.value;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });
      form.closest('.product-shell')?.querySelectorAll('[data-nuvia-product-reference]').forEach((reference) => {
        reference.classList.toggle('is-selected', reference.dataset.variantId === variantSelector.value);
      });
    };

    variantButtons.forEach((button) => {
      button.setAttribute('aria-pressed', 'false');
      button.addEventListener('click', () => {
        if (button.disabled) return;
        variantSelector.value = button.dataset.variantId || '';
        variantSelector.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    variantSelector.addEventListener('change', syncVariantButtons);
    syncVariantButtons();
  });

  const initBackToTopVisibility = () => {
    const backToTop = document.querySelector('[data-nuvia-back-to-top]');
    if (!backToTop) return;

    const mobileQuery = window.matchMedia('(max-width: 760px)');
    const syncVisibility = () => {
      const visible = !mobileQuery.matches || window.scrollY > Math.max(window.innerHeight * 1.15, 820);
      backToTop.classList.toggle('is-visible', visible);
      backToTop.setAttribute('aria-hidden', visible ? 'false' : 'true');
      backToTop.tabIndex = visible ? 0 : -1;
    };

    syncVisibility();
    window.addEventListener('scroll', syncVisibility, { passive: true });
    window.addEventListener('resize', syncVisibility);
  };

  initBackToTopVisibility();

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-nuvia-product-form]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (form.dataset.nuviaSubmitting === 'true') return;

    if (!syncProductSizeState(form)) {
      const hint = form.querySelector('[data-nuvia-quantity-hint]');
      showStatus(form, hint?.textContent || '請先選擇規格。', 'error');
      form.querySelector('.product-variant-fieldset')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }

    const submitter = event.submitter || form.querySelector('[type="submit"]');
    if (!submitter || submitter.disabled) return;
    const isBuyNow = submitter.matches('[data-nuvia-buy-now]');
    const defaultText = submitter.textContent.trim();
    const requestedVariant = selectedVariantData(form).value;
    const quantityInput = form.querySelector('[name="quantity"]');
    const requestedQuantity = Number(quantityInput.value);
    form.dataset.nuviaSubmitting = 'true';
    syncProductSizeState(form);
    submitter.classList.add('is-loading');
    submitter.textContent = isBuyNow ? '前往結帳中...' : '加入中...';
    showStatus(form, '');

    try {
      const beforeCart = await setCartCount();
      if (!beforeCart) throw new Error('Cart unavailable');
      syncProductCart(form, beforeCart);
      if (selectedVariantData(form).value !== requestedVariant) {
        showStatus(form, '規格已變更，請確認數量後再加入。', 'error');
        return;
      }
      if (!syncProductSizeState(form) || Number(quantityInput.value) !== requestedQuantity) {
        showStatus(form, '購物車數量已更新，請確認可購數量後再加入。', 'error');
        return;
      }
      const response = await fetch(`${routeRoot()}cart/add.js`, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) {
        // A 422 may still add the remaining stock: always read back before retrying.
        syncProductCart(form, await setCartCount());
        showStatus(form, response.status === 422 ? '可購數量已變動，請查看購物車中的實際數量，或重新整理頁面。' : '未能完成加入，請先查看購物車後再試。', 'error');
        return;
      }

      await flyToCart(form, submitter);
      syncProductCart(form, await setCartCount({ bump: true }));
      if (isBuyNow) {
        showStatus(form, '已加入購物車，正在前往結帳。');
        window.location.assign(`${routeRoot()}checkout`);
        return;
      }
      showStatus(form, '已加入購物車，可以繼續挑選或前往購物車結帳。');
    } catch (_) {
      // Network errors can occur after a successful add; do not encourage blind retries.
      try { syncProductCart(form, await setCartCount()); } catch (_) { /* Leave a clear recovery message. */ }
      showStatus(form, '連線中斷，請先查看購物車確認數量後再試。', 'error');
    } finally {
      delete form.dataset.nuviaSubmitting;
      submitter.classList.remove('is-loading');
      submitter.textContent = defaultText || '加入購物車';
      syncProductSizeState(form);
    }
  }, true);
})();
